import React, { useState } from "react";

import { IMAGE } from "@/shared/components/viewer/files";

import FullView from "./FullView";
import Link from "./Link";

/* A picture in an answer, `![words](https://…)`, fetched from its own server as it nears the
   view (that server sees the viewer's address — accepted; and whatever the address carries, the
   open risk in frontend/README.md "Pictures"). Its frame is 340px tall while it loads
   and stays so for any taller picture, which shrinks to fit, its width following the picture up
   to the answer's, so nothing below moves as a big picture arrives; a smaller one then takes its
   own height, the one move — 340px, the most a generated image shows (`ImageFile`), so both kinds
   of picture read alike. A click opens it in the app's viewer, unless the picture is a link's, which
   then opens the link. An address that isn't https, or a picture that doesn't load, is its words
   instead: a link where it may open, never a link inside a link. */

const HEIGHT = 340;

export default function AnswerImage({ src, alt, linked }: { src: string; alt: string; linked: boolean }) {
  const [height, setHeight] = useState<number | null>(null); // its own, once loaded
  const [failed, setFailed] = useState(false);
  const [full, setFull] = useState(false);
  const words = alt || "Image";
  if (!/^https:\/\//i.test(src) || failed) return linked ? <span>{words}</span> : <Link href={src}>{words}</Link>;
  // a line break written after the picture would leave an empty line under the block
  const frame = "block my-1 w-fit max-w-full rounded-[12px] overflow-hidden ring-1 ring-[var(--hairline)] bg-[var(--surface-sunk)] [&+br]:hidden";
  const style = { height: height !== null && height < HEIGHT ? undefined : HEIGHT };
  const picture = (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onLoad={(event) => setHeight(event.currentTarget.naturalHeight)}
      onError={() => setFailed(true)}
      className={`block h-full w-auto max-w-full object-contain ${height === null ? "min-w-[160px]" : ""}`}
    />
  );
  if (linked) return <span className={frame} style={style}>{picture}</span>;
  return (
    <>
      <button
        type="button"
        onClick={() => setFull(true)}
        aria-label={`Open ${alt || "the picture"} full size`}
        className={`press ${frame} p-0 cursor-zoom-in hover:ring-[var(--hairline-focus)]`}
        style={style}
      >
        {picture}
      </button>
      {full && (
        <FullView {...named(src, alt)} download={src} picture={{ src, alt }} onClose={() => setFull(false)} />
      )}
    </>
  );
}

/** The viewer's name for a picture — its file's when the address names a picture file, else its
    words — its type, and the site it is on. */
function named(src: string, alt: string): { title: string; kind: string; about: string } {
  const words = { title: alt || "Picture", kind: "image", about: "From the web" };
  try {
    const url = new URL(src);
    const file = decodeURIComponent(url.pathname.split("/").pop() ?? "");
    const kind = /\.(\w+)$/.exec(file)?.[1].toLowerCase() ?? "";
    return IMAGE.test(kind) ? { title: file, kind, about: url.hostname } : { ...words, about: url.hostname };
  } catch {
    return words; // an address the browser loads but can't take apart, such as a broken %-escape
  }
}
